import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { LotSizingLine } from '@/types/core/lot-sizing/lot-sizing-line';
import {
  buildDraftSavePayload,
  getDraftDisplayLine,
  type LotSizingPurchaseOrderDraftAddedLineById,
  type LotSizingPurchaseOrderDraftByLineId,
  type LotSizingPurchaseOrderDraftChange,
  type LotSizingPurchaseOrderDraftPatch,
  setDraftChange,
  type UpdateLotSizingPurchaseOrderLineInput,
} from './lot-sizing-purchase-order-draft';

type LotSizingPurchaseOrderDraftContextValue = {
  addDraftLine: (line: Omit<LotSizingLine, 'id'>) => void;
  draftAddedLines: LotSizingLine[];
  draftChanges: LotSizingPurchaseOrderDraftChange[];
  getAddedLines: (supplierId: number | string) => LotSizingLine[];
  hasChanges: boolean;
  hasDraftAddedProduct: (productId: number) => boolean;
  getDisplayLine: (line: LotSizingLine) => LotSizingLine;
  getSavePayload: () => UpdateLotSizingPurchaseOrderLineInput[];
  resetDraft: () => void;
  setLineChecked: (line: LotSizingLine, checked: boolean) => void;
  setLineQuantity: (line: LotSizingLine, quantity: number) => void;
};

const LotSizingPurchaseOrderDraftContext =
  createContext<LotSizingPurchaseOrderDraftContextValue | null>(null);

export function LotSizingPurchaseOrderDraftProvider({
  children,
}: PropsWithChildren) {
  const [draftByLineId, setDraftByLineId] =
    useState<LotSizingPurchaseOrderDraftByLineId>({});
  const [draftAddedLinesById, setDraftAddedLinesById] =
    useState<LotSizingPurchaseOrderDraftAddedLineById>({});
  const nextDraftLineIdRef = useRef(-1);

  const updateDraftLine = useCallback(
    (line: LotSizingLine, patch: LotSizingPurchaseOrderDraftPatch) => {
      setDraftByLineId((prev) => setDraftChange(prev, line, patch));
    },
    [],
  );

  const updateDraftAddedLine = useCallback(
    (line: LotSizingLine, patch: LotSizingPurchaseOrderDraftPatch) => {
      setDraftAddedLinesById((prev) => {
        const currentLine = prev[String(line.id)];
        if (currentLine === undefined) {
          return prev;
        }

        const nextLine = {
          ...currentLine,
          ...patch,
        };

        if (
          nextLine.quantity === currentLine.quantity &&
          nextLine.checked === currentLine.checked
        ) {
          return prev;
        }

        return {
          ...prev,
          [String(line.id)]: nextLine,
        };
      });
    },
    [],
  );

  const setLineQuantity = useCallback(
    (line: LotSizingLine, quantity: number) => {
      if (line.id < 0) {
        updateDraftAddedLine(line, { quantity });
        return;
      }

      updateDraftLine(line, { quantity });
    },
    [updateDraftAddedLine, updateDraftLine],
  );

  const setLineChecked = useCallback(
    (line: LotSizingLine, checked: boolean) => {
      if (line.id < 0) {
        updateDraftAddedLine(line, { checked });
        return;
      }

      updateDraftLine(line, { checked });
    },
    [updateDraftAddedLine, updateDraftLine],
  );

  const addDraftLine = useCallback((line: Omit<LotSizingLine, 'id'>) => {
    const draftLineId = nextDraftLineIdRef.current;
    nextDraftLineIdRef.current -= 1;

    setDraftAddedLinesById((prev) => ({
      ...prev,
      [String(draftLineId)]: {
        ...line,
        id: draftLineId,
      },
    }));
  }, []);

  const getAddedLines = useCallback(
    (supplierId: number | string): LotSizingLine[] =>
      Object.values(draftAddedLinesById).filter(
        (line) => String(line.supplierId) === String(supplierId),
      ),
    [draftAddedLinesById],
  );

  const hasDraftAddedProduct = useCallback(
    (productId: number): boolean =>
      Object.values(draftAddedLinesById).some(
        (line) => line.product.id === productId,
      ),
    [draftAddedLinesById],
  );

  const getDisplayLine = useCallback(
    (line: LotSizingLine): LotSizingLine =>
      getDraftDisplayLine(line, draftByLineId),
    [draftByLineId],
  );

  const getSavePayload = useCallback(
    (): UpdateLotSizingPurchaseOrderLineInput[] =>
      buildDraftSavePayload(draftByLineId, draftAddedLinesById),
    [draftAddedLinesById, draftByLineId],
  );

  const resetDraft = useCallback(() => {
    setDraftByLineId({});
    setDraftAddedLinesById({});
    nextDraftLineIdRef.current = -1;
  }, []);

  const hasChanges =
    Object.keys(draftByLineId).length > 0 ||
    Object.keys(draftAddedLinesById).length > 0;
  const draftAddedLines = useMemo(
    () => Object.values(draftAddedLinesById),
    [draftAddedLinesById],
  );
  const draftChanges = useMemo(
    () => Object.values(draftByLineId),
    [draftByLineId],
  );

  const value = useMemo(
    () => ({
      addDraftLine,
      draftAddedLines,
      draftChanges,
      getAddedLines,
      hasDraftAddedProduct,
      hasChanges,
      getDisplayLine,
      getSavePayload,
      resetDraft,
      setLineChecked,
      setLineQuantity,
    }),
    [
      addDraftLine,
      draftAddedLines,
      draftChanges,
      getAddedLines,
      getDisplayLine,
      getSavePayload,
      hasChanges,
      hasDraftAddedProduct,
      resetDraft,
      setLineChecked,
      setLineQuantity,
    ],
  );

  return (
    <LotSizingPurchaseOrderDraftContext.Provider value={value}>
      {children}
    </LotSizingPurchaseOrderDraftContext.Provider>
  );
}

export function useLotSizingPurchaseOrderDraft() {
  const context = useContext(LotSizingPurchaseOrderDraftContext);

  if (context === null) {
    throw new Error(
      'useLotSizingPurchaseOrderDraft must be used within its provider',
    );
  }

  return context;
}
